/* TenantHostContext — resolves the current Host header to a tenant on
 * boot, then exposes the result to every downstream component.
 *
 *   <TenantHostProvider>
 *     ...the rest of your app...
 *   </TenantHostProvider>
 *
 * The hook returns:
 *   - loading: true while the resolve-host request is in flight
 *   - tenant : null on the canonical verqo host, or a small dict with
 *              {id, slug, name, branding, tagline, host_kind}
 *   - mode   : "private" on tenant hosts (closed-network gating), null otherwise
 *   - isTenantHost: true iff the visitor is on a seller's vanity host
 *
 * Once tenant is known we also:
 *   1. Apply branding to CSS variables (--brand-primary, --brand-accent)
 *      so every shadcn surface picks them up automatically.
 *   2. Inject a <meta name="robots" content="noindex,nofollow"> tag and
 *      a per-tenant <link rel="icon"> if a logo is set.
 *   3. Override document.title with the seller's brand name.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { classifyHost, HOST_KIND } from "../lib/hostScope";

const TenantHostContext = createContext({ loading: true, tenant: null, mode: null, isTenantHost: false });

export function TenantHostProvider({ children }) {
  const [state, setState] = useState({ loading: true, tenant: null, mode: null });

  useEffect(() => {
    let alive = true;
    const host = window.location.host;
    // Only vanity / storefront hosts need a backend lookup to discover their
    // tenant. Canonical verqo hosts (marketing, buyers/sellers/ops portals,
    // dev/preview) are classified entirely client-side — so we render the app
    // INSTANTLY and stay fully resilient even if the backend is unreachable.
    // This prevents the whole site from getting stuck on "Loading…" (a blank
    // page) whenever an API call to resolve-host is slow or returns an error.
    if (classifyHost(host) !== HOST_KIND.STOREFRONT) {
      setState({ loading: false, tenant: null, mode: null });
      return () => { alive = false; };
    }
    api
      .get("/public/resolve-host", { params: { host }, timeout: 6000 })
      .then((r) => {
        if (!alive) return;
        setState({ loading: false, tenant: r.data?.tenant || null, mode: r.data?.mode || null });
      })
      .catch(() => {
        if (!alive) return;
        // Fail-open: treat as canonical host so nothing breaks if the
        // resolve endpoint is unreachable / slow for any reason.
        setState({ loading: false, tenant: null, mode: null });
      });
    return () => { alive = false; };
  }, []);

  const isTenantHost = !!state.tenant;

  // Apply branding side-effects (CSS vars, favicon, robots, title).
  useEffect(() => {
    if (!isTenantHost) return undefined;
    const t = state.tenant;
    const root = document.documentElement;
    if (t.branding?.primary_color) root.style.setProperty("--brand-primary", t.branding.primary_color);
    if (t.branding?.accent_color)  root.style.setProperty("--brand-accent",  t.branding.accent_color);
    // Favicon
    let favicon = document.querySelector('link[rel="icon"][data-tenant]');
    if (t.branding?.logo_url) {
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        favicon.dataset.tenant = "1";
        document.head.appendChild(favicon);
      }
      favicon.href = t.branding.logo_url;
    }
    // Robots noindex on every tenant host (closed network).
    let robots = document.querySelector('meta[name="robots"][data-tenant]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      robots.dataset.tenant = "1";
      document.head.appendChild(robots);
    }
    robots.content = "noindex,nofollow,noarchive";
    // Title
    if (t.name) document.title = t.name;
    return undefined;
  }, [isTenantHost, state.tenant]);

  const value = useMemo(
    () => ({ ...state, isTenantHost }),
    [state, isTenantHost],
  );
  return <TenantHostContext.Provider value={value}>{children}</TenantHostContext.Provider>;
}

export function useTenantHost() {
  return useContext(TenantHostContext);
}
